import { useRef, useState } from 'react';

/**
 * @typedef {Object} NewDocumentModalLock
 * @property {boolean} isLocked True while the modal must ignore ESC, clicks outside, Cancelar and the form.
 * @property {function(boolean): void} setIsUploadingAnnexes Toggles the annex upload flag; used by the annex table.
 * @property {function(): void} startAnnexUpload Keeps the modal locked until the annex upload finishes.
 * @property {function(): boolean} hasPendingAnnexUpload Whether the saved document still has annexes uploading.
 * @property {function(function(): Promise<*>, function(Error): void): Promise<*>} runLockedSave
 *   Runs a save with the modal locked; ignored while another save is running.
 * @property {function(): void} release Releases the whole lock (modal closed or annex upload failed).
 * @property {function(function(): *): void} runAfterAnnexUpload Runs an action now, or keeps it
 *   for runDeferredAction() while the annexes of the saved document are still uploading.
 * @property {function(): void} runDeferredAction Runs the action kept by runAfterAnnexUpload(), if any.
 */

/**
 * Lock of the "Nuevo documento" modal while a document is saved or signed and its annexes are
 * uploaded, so no user action can interrupt the process.
 *
 * @returns {NewDocumentModalLock}
 */
const useNewDocumentModalLock = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAnnexes, setIsUploadingAnnexes] = useState(false);
  // Refs, not state: the save flow reads them before React re-renders.
  const isSaveRunningRef = useRef(false);
  const hasPendingAnnexUploadRef = useRef(false);
  const deferredActionRef = useRef(null);

  /**
   * Keeps the modal locked until the annex upload of the saved document finishes.
   *
   * @returns {void}
   */
  const startAnnexUpload = () => {
    hasPendingAnnexUploadRef.current = true;
    setIsUploadingAnnexes(true);
  };

  /**
   * Whether the saved document still has annexes uploading.
   *
   * @returns {boolean}
   */
  const hasPendingAnnexUpload = () => hasPendingAnnexUploadRef.current;

  /**
   * Runs a save with the modal locked. While annexes are still uploading when the save ends, the
   * lock is kept and released by the annex upload outcome. A call made while another save is
   * running is ignored and leaves that save's lock untouched.
   *
   * @param {function(): Promise<*>} saveAction Saves the document.
   * @param {function(Error): void} onError Reports an error thrown by saveAction.
   * @returns {Promise<*>} What saveAction resolves to; undefined when ignored or when it fails.
   */
  const runLockedSave = async (saveAction, onError) => {
    if (isSaveRunningRef.current) {
      return undefined;
    }
    isSaveRunningRef.current = true;
    hasPendingAnnexUploadRef.current = false;
    setIsSaving(true);
    try {
      return await saveAction();
    } catch (error) {
      onError(error);
      return undefined;
    } finally {
      isSaveRunningRef.current = false;
      if (!hasPendingAnnexUploadRef.current) {
        setIsSaving(false);
      }
    }
  };

  /**
   * Releases the whole lock so the user can retry or close the modal.
   *
   * @returns {void}
   */
  const release = () => {
    hasPendingAnnexUploadRef.current = false;
    setIsSaving(false);
    setIsUploadingAnnexes(false);
  };

  /**
   * Runs an action now or, while the annexes are still uploading, keeps it for
   * runDeferredAction() so the modal stays available to retry them.
   *
   * @param {function(): *} action Action to run once the annex upload is done.
   * @returns {void}
   */
  const runAfterAnnexUpload = (action) => {
    if (hasPendingAnnexUploadRef.current) {
      deferredActionRef.current = action;
      return;
    }
    action();
  };

  /**
   * Runs the action kept by runAfterAnnexUpload(), if any; meant for when the modal closes.
   *
   * @returns {void}
   */
  const runDeferredAction = () => {
    const deferredAction = deferredActionRef.current;
    deferredActionRef.current = null;
    if (deferredAction) {
      deferredAction();
    }
  };

  return {
    isLocked: isSaving || isUploadingAnnexes,
    setIsUploadingAnnexes,
    startAnnexUpload,
    hasPendingAnnexUpload,
    runLockedSave,
    release,
    runAfterAnnexUpload,
    runDeferredAction,
  };
};

export default useNewDocumentModalLock;
